/**
 * ErrorReportContext
 * 
 * Provides app-wide access to showError() so any component can trigger
 * the centralized error modal without prop drilling.
 * 
 * Renders a single ErrorReportModal instance at the provider level.
 * 
 * Usage:
 *   import { useErrorReportContext } from '@/context/ErrorReportContext';
 *   const { showError } = useErrorReportContext();
 * 
 *   // System error (shows report UI)
 *   showError({ title: 'Something Went Wrong', message: err.message, type: 'system' });
 * 
 *   // Validation error (shows OK dismiss)
 *   showError({ title: 'Missing Information', message: 'Please fill in all required fields', type: 'validation' });
 */

"use client";

import React, { createContext, useContext, useCallback } from 'react';
import { useErrorReport, type ShowErrorOptions } from '@/hooks/ui/useErrorReport';
import { useNotification } from '@/components/ui/notification/NotificationProvider';
import ErrorReportModal from '@/components/ui/ErrorReportModal';

/** Matches the 403 message from rejectDemoWriteSync / rejectDemoWrite. */
const DEMO_MODE_ERROR_FRAGMENT = 'disabled in demo mode';

interface ErrorReportContextType {
  showError: (options: ShowErrorOptions) => void;
}

const ErrorReportContext = createContext<ErrorReportContextType | undefined>(undefined);

export function ErrorReportProvider({ children }: { children: React.ReactNode }) {
  const { showError: showErrorRaw, errorState, clearError, submitReport, isSubmitting } = useErrorReport();
  const { showNotification } = useNotification();

  // Intercept demo-mode errors and show a friendly toast instead of the bug report modal
  const showError = useCallback((options: ShowErrorOptions) => {
    if (options.message?.toLowerCase().includes(DEMO_MODE_ERROR_FRAGMENT)) {
      showNotification(
        'warning',
        'Demo Mode',
        'Editing is disabled in demo mode. Exit demo mode to make changes to your own gym.',
        6000,
      );
      return;
    }
    showErrorRaw(options);
  }, [showErrorRaw, showNotification]);

  return (
    <ErrorReportContext.Provider value={{ showError }}>
      {children}
      <ErrorReportModal
        errorState={errorState}
        onClose={clearError}
        onSubmitReport={submitReport}
        isSubmitting={isSubmitting}
      />
    </ErrorReportContext.Provider>
  );
}

export function useErrorReportContext(): ErrorReportContextType {
  const context = useContext(ErrorReportContext);
  if (!context) {
    throw new Error('useErrorReportContext must be used within an ErrorReportProvider');
  }
  return context;
}
